/* eslint-disable eqeqeq */
import { useParams } from "react-router-dom";
import { useMemo, useState, useCallback } from "react";
import { BackButton, ModalCloseBtn } from "../../../primaryComponents";
import { useGetCategoriesLinkedToProduct } from "../../../../hooks/api/queries";
import styles from "./styles.module.scss";
import { truncateString } from "../../../../helpers";
import { useSellModeFilters } from "../../../../hooks/useSellModeFilters";
import primaryComponents from "../../../primaryComponents";
import secondaryComponents from "../..";

export const AllCategoriesView = ({
  closeModal = () => {},
  isMobileView = false,
  category,
  sub = false,
}) => {
  const { store_id } = useParams();
  const { data: categories = [], isLoading } = useGetCategoriesLinkedToProduct({
    queries: `?store_id=${store_id}`,
  });
  const categoriesToRender = category ? category?.sub_categories || [] : [];
  const { search, filterByCategory } = useSellModeFilters();
  const [value, setValue] = useState("");
  const [currentCategory, setCurrentCategory] = useState(category);

  const activeList = sub
    ? categoriesToRender
    : currentCategory?.sub_categories || categories;

  const filteredList = useMemo(
    () =>
      activeList.filter((item) =>
        item?.name?.toLowerCase()?.includes(value?.trim()?.toLowerCase()),
      ),
    [activeList, value],
  );

  const handleCategorySelect = useCallback(
    (category, includeSub = false, isRootAll = false) => {
      if (isRootAll) {
        filterByCategory({ id: null, include_sub_category_products: false });
        closeModal();
        return;
      }

      if (includeSub && category?.id) {
        if (category?.parent_category) {
          filterByCategory({
            id: category.id,
            parentId: category.parent_category.id,
            include_sub_category_products: true,
            isSub: true,
          });
        } else {
          filterByCategory({
            id: category.id,
            include_sub_category_products: true,
          });
        }
        closeModal();
        return;
      }

      if (category?.sub_categories?.length > 0) {
        setCurrentCategory(category);
        setValue("");
        return;
      }

      if (category?.parent_category) {
        filterByCategory({
          id: category.id,
          parentId: category.parent_category.id,
          include_sub_category_products: false,
          isSub: true,
        });
      } else {
        filterByCategory({
          id: category?.id ?? null,
          include_sub_category_products: false,
        });
      }
      closeModal();
    },
    [filterByCategory, closeModal],
  );

  // Active states
  const activeCategoryId = search.get("category_id");
  const activeSubCategoryId = search.get("sub_category_id");
  const includeSubProducts = !!search.get("include_sub_category_products");

  return (
    <section
      aria-labelledby="category-heading"
      className={`${styles.allCategoriesView} ${
        isMobileView ? styles.mobileView : ""
      }`}
    >
      {/* Header */}
      {!isMobileView ? (
        <>
          <div className={styles.allCategoriesView__header}>
            {currentCategory ? (
              <div
                className="d-flex align-items-center"
                style={{ gap: "15px" }}
              >
                {!sub ? (
                  <BackButton
                    onClick={() => setCurrentCategory(null)}
                    text="Back"
                  />
                ) : null}
                <h4 id="category-heading" className="mb-0">
                  {currentCategory.name}
                </h4>
              </div>
            ) : (
              <h4 id="category-heading" className="mb-0">
                Select a Category
              </h4>
            )}
            {!currentCategory && <ModalCloseBtn onClick={closeModal} />}
            {sub && <ModalCloseBtn onClick={closeModal} />}
          </div>
          <hr />
        </>
      ) : (
        <div>
          <div className={styles.currentSelection}>
            <span className="dark--text font-weight-normal">
              Current Selection:
            </span>
            <h6 className="mb-0">
              {filteredList?.find((item) => `${item?.id}` === activeCategoryId)
                ?.name || "All"}
            </h6>
          </div>
        </div>
      )}

      {/* Search */}
      <div className={styles.allCategoriesView__search}>
        <primaryComponents.SearchField
          showsearchBtn={false}
          showClearBtn={true}
          triggerOnInput={true}
          placeholder={
            currentCategory
              ? `Search sub category in ${currentCategory.name}`
              : "Search Category"
          }
          useBorderImage={true}
          borderColor="#1e016f"
          value={value}
          trigger={setValue}
        />
      </div>
      <hr />

      {/* Category / Subcategory List */}
      <secondaryComponents.LoaderHelper isLoading={isLoading} classNames="py-4">
        <div className={styles.allCategoriesView__items} aria-live="polite">
          {!currentCategory ? (
            sub && category ? (
              // Root All
              <button
                type="button"
                className={`${styles.category} ${
                  activeCategoryId == category.id && includeSubProducts
                    ? styles.active
                    : ""
                }`}
                onClick={() => handleCategorySelect(category, false, true)}
                aria-pressed={
                  activeCategoryId == category.id && includeSubProducts
                }
              >
                All
              </button>
            ) : (
              <button
                type="button"
                className={`${styles.category} ${
                  !activeCategoryId ? styles.active : ""
                }`}
                onClick={() => handleCategorySelect(null, false, true)}
                aria-pressed={!activeCategoryId}
              >
                All
              </button>
            )
          ) : (
            <button
              type="button"
              className={`${styles.category} ${
                activeCategoryId == currentCategory.id && includeSubProducts
                  ? styles.active
                  : ""
              }`}
              onClick={() => handleCategorySelect(currentCategory, true)}
              aria-pressed={
                activeCategoryId == currentCategory.id && includeSubProducts
              }
            >
              All
            </button>
          )}

          {/* Render filtered list */}
          {filteredList.map((item) => {
            const isActive = item?.parent_category
              ? activeSubCategoryId == item.id && !includeSubProducts
              : activeCategoryId == item.id && !includeSubProducts;

            const isParentActive =
              activeCategoryId == item?.id && includeSubProducts;

            return (
              <button
                key={item.id}
                className={`${styles.category} ${
                  isActive || isParentActive ? styles.active : ""
                }`}
                onClick={() => handleCategorySelect(item)}
                aria-pressed={isActive || isParentActive}
              >
                <span>{truncateString(item?.name, 50)}</span>
              </button>
            );
          })}
        </div>
      </secondaryComponents.LoaderHelper>
    </section>
  );
};
